// Client API centralisé — fetch avec cookies httpOnly.
// Intercepteur 401 : tente un refresh (single-flight) avant de propager l'erreur.
const BASE = process.env.REACT_APP_BACKEND_URL || "";

const DEFAULT_ERROR = "Une erreur est survenue. Veuillez réessayer.";

function formatDetail(detail) {
  if (detail == null) return DEFAULT_ERROR;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

let _refreshInFlight = null;
async function _refreshOnce() {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const res = await fetch(`${BASE}/api/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // libère après un court délai pour dédupliquer les appels concurrents
      setTimeout(() => { _refreshInFlight = null; }, 0);
    }
  })();
  return _refreshInFlight;
}

async function request(path, { method = "GET", body, headers, _retried } = {}) {
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const finalHeaders = { ...(headers || {}) };
  if (!isForm) finalHeaders["Content-Type"] = "application/json";

  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers: finalHeaders,
    body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
  });

  // Intercepteur 401 — on tente UNE seule fois un refresh puis on rejoue.
  // Ne pas retry pour /auth/refresh, /auth/login, /auth/logout eux-mêmes.
  if (
    res.status === 401 &&
    !_retried &&
    !path.startsWith("/api/auth/refresh") &&
    !path.startsWith("/api/auth/login") &&
    !path.startsWith("/api/auth/logout")
  ) {
    const ok = await _refreshOnce();
    if (ok) {
      return request(path, { method, body, headers, _retried: true });
    }
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const detail = data?.detail;
    const err = new Error(formatDetail(detail));
    err.status = res.status;
    err.data = data;
    // Notifie l'app (AuthContext) qu'un 401 non récupérable est arrivé
    if (res.status === 401 && !path.startsWith("/api/auth/")) {
      try {
        window.dispatchEvent(new CustomEvent("wyvio:auth-expired"));
      } catch { /* ignore */ }
    }
    throw err;
  }
  return data;
}

export const api = {
  // Auth agence
  login: (email, password) =>
    request("/api/auth/login", { method: "POST", body: { email, password } }),
  logout: () => request("/api/auth/logout", { method: "POST" }),
  me: () => request("/api/auth/me"),
  getMe: () => request("/api/auth/me"),
  forgotPassword: (email) =>
    request("/api/auth/password/forgot", { method: "POST", body: { email } }),
  resetPassword: (token, newPassword) =>
    request("/api/auth/password/reset", {
      method: "POST",
      body: { token, new_password: newPassword },
    }),
  refreshToken: () => request("/api/auth/refresh", { method: "POST" }),
  changeEmail: (currentPassword, newEmail) =>
    request("/api/auth/change-email", {
      method: "POST",
      body: { current_password: currentPassword, new_email: newEmail },
    }),

  // Uploads (Production readiness)
  uploadAgencyLogo: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return request("/api/uploads/agency-logo", { method: "POST", body: fd });
  },
  uploadUserPhoto: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return request("/api/uploads/user-photo", { method: "POST", body: fd });
  },
  sendSupportMessage: (payload) =>
    request("/api/support/message", { method: "POST", body: payload }),

  // Agence
  getAgency: () => request("/api/agency"),
  updateAgency: (patch) =>
    request("/api/agency", { method: "PATCH", body: patch }),
  listAgencyUsers: () =>
    request("/api/agency/users").then((r) => (Array.isArray(r) ? r : r?.users || [])),

  // Dossiers agence
  listDossiers: () => request("/api/dossiers"),
  createDossier: (payload) =>
    request("/api/dossiers", { method: "POST", body: payload }),
  updateDossier: (id, patch) =>
    request(`/api/dossiers/${id}`, { method: "PATCH", body: patch }),
  getDossier: (id) => request(`/api/dossiers/${id}`),
  getDossierByOrder: (orderNumber) =>
    request(`/api/dossiers/by-order/${orderNumber}`),
  getJournal: (id) => request(`/api/dossiers/${id}/journal`),
  updateStep: (dossierId, stepKey, patch) =>
    request(`/api/dossiers/${dossierId}/steps/${stepKey}`, {
      method: "PATCH",
      body: patch,
    }),
  sendAgencyMessage: (dossierId, text) =>
    request(`/api/dossiers/${dossierId}/messages`, {
      method: "POST",
      body: { text },
    }),
  // Sprint P0.1 — mark-read réservé agence
  markAgencyMessagesRead: (dossierId, upToId = null) =>
    request(`/api/dossiers/${dossierId}/messages/mark-read`, {
      method: "POST",
      body: upToId ? { up_to_id: upToId } : {},
    }),
  createMagicLink: (dossierId) =>
    request(`/api/dossiers/${dossierId}/magic-link`),

  // Onboarding client (agence)
  resendClientInvitation: (dossierId) =>
    request(`/api/dossiers/${dossierId}/client/resend-invitation`, { method: "POST" }),
  generateClientMagicLink: (dossierId) =>
    request(`/api/dossiers/${dossierId}/client/magic-link`, { method: "POST" }),
  resetClientPassword: (dossierId) =>
    request(`/api/dossiers/${dossierId}/client/reset-password`, { method: "POST" }),
  getClientStatus: (dossierId) =>
    request(`/api/dossiers/${dossierId}/client/status`),
  uploadDocument: (dossierId, file, displayName, clientVisible, stepKey) => {
    const form = new FormData();
    form.append("file", file);
    if (displayName) form.append("display_name", displayName);
    form.append("client_visible", clientVisible ? "true" : "false");
    if (stepKey) form.append("step_key", stepKey);
    return request(`/api/dossiers/${dossierId}/documents`, {
      method: "POST",
      body: form,
    });
  },
  uploadMedia: (dossierId, file, displayName, clientVisible, stepKey) => {
    const form = new FormData();
    form.append("file", file);
    if (displayName) form.append("display_name", displayName);
    form.append("client_visible", clientVisible ? "true" : "false");
    if (stepKey) form.append("step_key", stepKey);
    return request(`/api/dossiers/${dossierId}/media`, {
      method: "POST",
      body: form,
    });
  },

  // Workflow templates
  listWorkflowTemplates: () => request("/api/workflows"),
  getWorkflowTemplate: (id) => request(`/api/workflows/${id}`),
  previewWorkflowTemplate: (id) => request(`/api/workflows/${id}/preview`),
  duplicateWorkflowTemplate: (sourceId, name) =>
    request("/api/workflows/duplicate", { method: "POST", body: { source_id: sourceId, name } }),
  createBlankWorkflow: (name) =>
    request("/api/workflows/blank", { method: "POST", body: { name } }),
  updateWorkflowTemplate: (id, patch) =>
    request(`/api/workflows/${id}`, { method: "PATCH", body: patch }),
  archiveWorkflowTemplate: (id) =>
    request(`/api/workflows/${id}/archive`, { method: "POST" }),
  activateWorkflowTemplate: (id) =>
    request(`/api/workflows/${id}/activate`, { method: "POST" }),
  deleteWorkflowTemplate: (id) =>
    request(`/api/workflows/${id}`, { method: "DELETE" }),

  // Dashboard control center (Lot 11)
  getDashboardSummary: (commercialId) =>
    request(`/api/dashboard/summary${commercialId ? `?commercial_id=${commercialId}` : ""}`),
  getCommandCenter: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/dashboard/command-center${qs ? `?${qs}` : ""}`);
  },
  getPlanning: (range = "week", commercialId) => {
    const p = new URLSearchParams({ range });
    if (commercialId) p.set("commercial_id", commercialId);
    return request(`/api/dashboard/planning?${p.toString()}`);
  },

  // Media Hub (Lot 12)
  mediaTimeline: (dossierId, { includeArchived, category, stepKey } = {}) => {
    const p = new URLSearchParams();
    if (includeArchived) p.set("include_archived", "true");
    if (category) p.set("category", category);
    if (stepKey) p.set("step_key", stepKey);
    return request(`/api/dossiers/${dossierId}/media-hub/timeline?${p.toString()}`);
  },
  patchMediaItem: (dossierId, itemId, patch) => {
    const form = new FormData();
    Object.entries(patch).forEach(([k, v]) => {
      if (v !== null && v !== undefined) form.append(k, typeof v === "boolean" ? String(v) : v);
    });
    return request(`/api/dossiers/${dossierId}/media-hub/${itemId}`, { method: "PATCH", body: form });
  },
  archiveMediaItem: (dossierId, itemId) =>
    request(`/api/dossiers/${dossierId}/media-hub/${itemId}/archive`, { method: "POST" }),
  restoreMediaItem: (dossierId, itemId) =>
    request(`/api/dossiers/${dossierId}/media-hub/${itemId}/restore`, { method: "POST" }),
  clientUpload: (file, displayName, stepKey, category) => {
    const form = new FormData();
    form.append("file", file);
    if (displayName) form.append("display_name", displayName);
    if (stepKey) form.append("step_key", stepKey);
    if (category) form.append("category", category);
    return request("/api/client/dossier/upload", { method: "POST", body: form });
  },

  // Communication Engine (Lot 13)
  listCommunications: (dossierId) =>
    request(`/api/dossiers/${dossierId}/communications`),
  sendCommunication: (dossierId, payload) =>
    request(`/api/dossiers/${dossierId}/communications/send`,
            { method: "POST", body: payload }),
  getCommsSettings: () => request("/api/agency/communications/settings"),
  patchCommsSettings: (payload) =>
    request("/api/agency/communications/settings",
            { method: "PATCH", body: payload }),

  // Payment (Lot 14A)
  getPayment: (dossierId) =>
    request(`/api/dossiers/${dossierId}/payment`),
  patchPaymentExpectations: (dossierId, payload) =>
    request(`/api/dossiers/${dossierId}/payment/expectations`,
            { method: "PATCH", body: payload }),
  confirmPayment: (dossierId, payload) =>
    request(`/api/dossiers/${dossierId}/payment/confirm`,
            { method: "POST", body: payload }),
  remindPayment: (dossierId, payload) =>
    request(`/api/dossiers/${dossierId}/payment/remind`,
            { method: "POST", body: payload }),
  clientDeclarePayment: (payload) =>
    request("/api/client/payment/declare", { method: "POST", body: payload }),
  clientGetPayment: () => request("/api/client/payment"),
  // Sprint UI 1.0 — intention "règlement en agence" (indépendante du status)
  settlePaymentInAgency: (dossierId, value) =>
    request(`/api/dossiers/${dossierId}/payment/settle-in-agency`,
            { method: "POST", body: { value: !!value } }),
  clientSettlePaymentInAgency: (value) =>
    request("/api/client/payment/settle-in-agency",
            { method: "POST", body: { value: !!value } }),

  // Multi-users / Team (Lot 15)
  listRoles: () => request("/api/roles"),
  listTeam: (includeArchived = false) =>
    request(`/api/agency/users?include_archived=${includeArchived ? "true" : "false"}`),
  inviteTeamMember: (payload) =>
    request("/api/agency/users/invite", { method: "POST", body: payload }),
  resendInvitation: (userId) =>
    request(`/api/agency/users/${userId}/resend-invitation`, { method: "POST", body: {} }),
  patchTeamMember: (userId, payload) =>
    request(`/api/agency/users/${userId}`, { method: "PATCH", body: payload }),
  setTeamMemberStatus: (userId, status, reason = null) =>
    request(`/api/agency/users/${userId}/status`,
            { method: "PATCH", body: { status, reason } }),
  transferOwnership: (userId) =>
    request("/api/agency/users/transfer-ownership",
            { method: "POST", body: { user_id: userId } }),
  agencyAudit: (limit = 100) =>
    request(`/api/agency/audit?limit=${limit}`),
  peekInvitation: (token) => request(`/api/collab/invitation/${token}`),
  activateInvitation: (token, password) =>
    request("/api/collab/activate",
            { method: "POST", body: { token, password } }),

  // Twilio (Lot 15B) — legacy per-agency creds (kept for backward-compat)
  patchTwilioCredentials: (payload) =>
    request("/api/agency/communications/sms/credentials",
            { method: "PATCH", body: payload }),
  clearTwilioCredentials: () =>
    request("/api/agency/communications/sms/credentials",
            { method: "DELETE" }),
  testTwilioConnection: (phone) =>
    request("/api/agency/communications/sms/test",
            { method: "POST", body: { phone } }),

  // Sinch platform (Lot 16) — platform-scoped
  platformGetSinch: () => request("/api/platform/communications/sinch"),
  platformPatchSinch: (payload) =>
    request("/api/platform/communications/sinch",
            { method: "PATCH", body: payload }),
  platformTestSinch: (phone) =>
    request("/api/platform/communications/sinch/test",
            { method: "POST", body: { phone } }),
  platformLiveTestSinch: (phone, body) =>
    request("/api/platform/communications/sinch/livetest",
            { method: "POST", body: { phone, body: body || null } }),
  platformListSinchMessages: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/platform/communications/sinch/messages${qs ? `?${qs}` : ""}`);
  },

  // Lot 18 finalisation
  platformListSmsTemplates: () => request("/api/platform/communications/sms/templates"),
  platformUpdateSmsTemplate: (key, patch) =>
    request(`/api/platform/communications/sms/templates/${key}`,
            { method: "PATCH", body: patch }),
  platformResetSmsTemplate: (key) =>
    request(`/api/platform/communications/sms/templates/${key}/reset`,
            { method: "POST" }),
  platformSmsPreview: (body, ctx = null) =>
    request("/api/platform/communications/sms/preview",
            { method: "POST", body: { body, ctx } }),
  platformListSmsJournal: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/platform/communications/sms/journal${qs ? `?${qs}` : ""}`);
  },
  platformSmsKpi:       () => request("/api/platform/communications/sms/kpi"),
  platformListChannels: () => request("/api/platform/communications/channels"),
  agencyListSmsTemplates:  () => request("/api/platform/my-agency/sms-templates"),
  agencyUpdateSmsTemplate: (key, patch) =>
    request(`/api/platform/my-agency/sms-templates/${key}`,
            { method: "PATCH", body: patch }),

  // Lot 19 — Router & multi-provider
  platformListProviders: () => request("/api/platform/communications/providers"),
  platformPatchProvider: (name, patch) =>
    request(`/api/platform/communications/providers/${name}`,
            { method: "PATCH", body: patch }),
  platformResetProviderHealth: (name) =>
    request(`/api/platform/communications/providers/${name}/health/reset`,
            { method: "POST" }),
  platformCommsAnalytics: (days = 30) =>
    request(`/api/platform/communications/analytics?days=${days}`),
  agencyCommsSettings: () => request("/api/platform/my-agency/communications/settings"),
  agencyToggleTrigger: (trigger, enabled) =>
    request(`/api/platform/my-agency/communications/settings/triggers/${trigger}`,
            { method: "PATCH", body: { enabled } }),
  agencyToggleChannel: (channel, enabled) =>
    request(`/api/platform/my-agency/communications/settings/channels/${channel}`,
            { method: "PATCH", body: { enabled } }),
  getDossierTimeline: (id, { limit = 50, before = null } = {}) => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (before) p.set("before", before);
    return request(`/api/dossiers/${id}/timeline?${p.toString()}`);
  },

  // ─── Lot 21 · Dossier SAV Premium ────────────────────────────────────
  listSavForDossier: (id) => request(`/api/dossiers/${id}/sav`),
  createSavForDossier: (id, payload) =>
    request(`/api/dossiers/${id}/sav`, { method: "POST", body: payload }),
  getSav: (sid) => request(`/api/sav/${sid}`),
  patchSav: (sid, payload) =>
    request(`/api/sav/${sid}`, { method: "PATCH", body: payload }),
  closeSav: (sid, resolutionSummary) =>
    request(`/api/sav/${sid}/close`, {
      method: "POST",
      body: { resolution_summary: resolutionSummary || null },
    }),
  sendSavMessage: (sid, text) =>
    request(`/api/sav/${sid}/messages`, { method: "POST", body: { text } }),
  updateSavStep: (sid, stepKey, patch) =>
    request(`/api/sav/${sid}/steps/${stepKey}`, {
      method: "PATCH",
      body: patch,
    }),
  getSavTimeline: (sid, { limit = 100 } = {}) =>
    request(`/api/sav/${sid}/timeline?limit=${limit}`),
  // client side
  clientListSavCases: () => request(`/api/client/sav-cases`),
  clientGetSav: (sid) => request(`/api/client/sav/${sid}`),
  clientSendSavMessage: (sid, text) =>
    request(`/api/client/sav/${sid}/messages`, {
      method: "POST",
      body: { text },
    }),

  // ─── Lot 22 · Command Center Screen & Display Tokens ─────────────────
  listDisplayTokens: () => request(`/api/agency/display-tokens`),
  createDisplayToken: (payload) =>
    request(`/api/agency/display-tokens`, { method: "POST", body: payload }),
  revokeDisplayToken: (id) =>
    request(`/api/agency/display-tokens/${id}`, { method: "DELETE" }),
  getCommandCenterScreen: (mode = "today") =>
    request(`/api/command-center/screen?mode=${mode}`),
  getCommandCenterConflicts: () =>
    request(`/api/command-center/conflicts`),
  overrideCommandCenterConflict: (payload) =>
    request(`/api/command-center/conflicts/override`, {
      method: "POST",
      body: payload,
    }),
  platformListQuotas: () => request("/api/platform/quotas"),
  platformSetQuota: (agencyId, payload) =>
    request(`/api/platform/agencies/${agencyId}/sms-quota`,
            { method: "PATCH", body: payload }),
  myAgencyQuota: () => request("/api/platform/my-agency/sms-quota"),

  // Client (lien sécurisé)
  clientLogin: (orderNumber, accessCode) =>
    request("/api/client/login", {
      method: "POST",
      body: { order_number: orderNumber, access_code: accessCode },
    }),
  clientSession: () => request("/api/client/session"),
  clientLogout: () => request("/api/client/logout", { method: "POST" }),
  clientDossier: () => request("/api/client/dossier"),
  clientDossiers: () => request("/api/client/dossiers"),
  clientDossierById: (id) => request(`/api/client/dossier/${id}`),
  clientMagic: (token) => request(`/api/client/magic/${token}`),
  // Nouveau : magic link 24h single-use
  clientMagicConsume: (token) =>
    request("/api/client/magic/consume", { method: "POST", body: { token } }),
  clientLoginV2: (email, password, requestMagicLink = false) =>
    request("/api/client/login-v2", {
      method: "POST",
      body: { email, password, request_magic_link: requestMagicLink },
    }),
  clientSetPassword: (password) =>
    request("/api/client/password/set", { method: "POST", body: { password } }),
  clientSendMessage: (text) =>
    request("/api/client/messages", { method: "POST", body: { text } }),

  // Sprint V2 — Planification des créneaux de livraison
  getDelivery: (dossierId) =>
    request(`/api/dossiers/${dossierId}/delivery`),
  proposeDelivery: (dossierId, { slots, location, notes }) =>
    request(`/api/dossiers/${dossierId}/delivery/propose`, {
      method: "POST", body: { slots, location, notes },
    }),
  scheduleDelivery: (dossierId, { date, time, location, notes }) =>
    request(`/api/dossiers/${dossierId}/delivery/schedule`, {
      method: "POST", body: { date, time, location, notes },
    }),
  cancelDelivery: (dossierId) =>
    request(`/api/dossiers/${dossierId}/delivery/cancel`, { method: "POST" }),
  clientGetDelivery: () =>
    request(`/api/client/delivery`),
  clientSelectSlot: (slotId) =>
    request(`/api/client/delivery/select`, {
      method: "POST", body: { slot_id: slotId },
    }),

  // Sprint V2 — Photos intelligentes (Gemini vision)
  uploadPhoto: async (dossierId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const url = `${BASE}/api/dossiers/${dossierId}/media`;
    const res = await fetch(url, { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
    return res.json();
  },
  reclassifyPhoto: (dossierId, mediaId, category) =>
    request(`/api/dossiers/${dossierId}/media/${mediaId}/reclassify`, {
      method: "PATCH", body: { category },
    }),
  deletePhoto: (dossierId, mediaId) =>
    request(`/api/dossiers/${dossierId}/media/${mediaId}`, { method: "DELETE" }),
};

export default api;
